import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { ProductListingRow, type RowAction } from '@vizit/molecules';
import * as React from 'react';
import { Table, TableBody } from 'react-aria-components';
import { productTableWrapperClasses, tableClasses, tableContainerClasses } from './product-listing-table.classes';
import { ProductListingTableHeader } from './product-listing-table.header';
import { ProductListingTableThead } from './product-listing-table.th';

const ROW_HEIGHT = 80; // Approximate height of each row (py-5 = 20px + 56px thumbnail)
const HEADER_HEIGHT = 200; // Approximate height for header and other elements

/**
 * Loading skeleton component for the product listing table
 */
function ProductListingTableSkeleton({ actions = [] }: { actions?: RowAction[] }) {
  const [skeletonRows, setSkeletonRows] = React.useState(5);
  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);

  React.useEffect(() => {
    const calculateRows = () => {
      const viewportHeight = window.innerHeight;
      const availableHeight = viewportHeight - HEADER_HEIGHT;
      const calculatedRows = Math.max(3, Math.floor(availableHeight / ROW_HEIGHT));
      setSkeletonRows(calculatedRows);
    };

    calculateRows();
    window.addEventListener('resize', calculateRows);
    return () => window.removeEventListener('resize', calculateRows);
  }, []);

  return (
    <div className={productTableWrapperClasses()}>
      <ProductListingTableHeader isLoading={true} />
      <div className={tableContainerClasses()}>
        <Table className={tableClasses({ withRetailer: isRetailerEnabled })} aria-label="Loading product listing table">
          <ProductListingTableThead showActionColumn={actions.length > 0} />
          <TableBody>
            {Array.from({ length: skeletonRows }, (_, index) => (
              <ProductListingRow
                // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton rows don't reorder and have no stable IDs
                key={`skeleton-row-${index}`}
                item={{
                  id: `skeleton-row-${index}`,
                  imageSrc: null,
                  productName: '',
                  asin: '',
                  score: null,
                  scoreStatus: null,
                  sourceType: null,
                  status: null,
                  lastRefreshed: null,
                  lastScraped: null,
                  createdAt: new Date(),
                }}
                isLoading={true}
                actions={actions}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export { ProductListingTableSkeleton };
